/**
 * The Loom — d3 force-graph renderer.
 *
 * A typed, scoped port of the validated prototype logic
 * (prototypes/agent-graph/index.html). `renderLoom(root, graph)` wires the
 * force simulation, filters, focus/search, click-through detail panel and the
 * "watch the weave grow" timeline onto elements it finds *within* `root`
 * (queried by `data-el`, never global IDs), and returns a cleanup function that
 * tears the simulation and listeners down. All rendering is imperative d3 — the
 * React page just owns the container skeleton.
 */
import * as d3 from 'd3'
import type { LoomGraph, LoomNode, LoomNodeType, LoomLinkKind } from './loom-graph'
import { escapeHtml as esc } from './loom-escape'

type SimNode = LoomNode & d3.SimulationNodeDatum
interface SimLink extends d3.SimulationLinkDatum<SimNode> {
  kind: LoomLinkKind
  real: boolean
  via?: string
  born?: string | null
}

const COLORS: Record<LoomNodeType, string> = {
  agent: '#a78bfa',
  tool: '#4ec9b0',
  namespace: '#e0a458',
  learning: '#f472b6',
}
const LINK_COLORS: Record<LoomLinkKind, string> = {
  owns: '#4ec9b0',
  reads: '#e0a458',
  writes: '#c2703a',
  delegates: '#a78bfa',
  learned: '#f472b6',
}
const TYPE_LABEL: Record<LoomNodeType, string> = {
  agent: 'Agents',
  tool: 'Skills / tools',
  namespace: 'Memory namespaces',
  learning: 'Learnings (mock)',
}
const LINK_LABEL: Record<LoomLinkKind, string> = {
  owns: 'owns',
  reads: 'reads',
  writes: 'writes',
  delegates: 'delegates',
  learned: 'learned',
}

const NODE_TYPES: LoomNodeType[] = ['agent', 'tool', 'namespace', 'learning']
const LINK_KINDS: LoomLinkKind[] = ['owns', 'reads', 'writes', 'delegates', 'learned']

const asNode = (endpoint: SimLink['source']): SimNode => endpoint as SimNode

export function renderLoom(root: HTMLElement, G: LoomGraph): () => void {
  const el = <T extends Element>(name: string): T => {
    const found = root.querySelector(`[data-el="${name}"]`)
    if (!found) throw new Error(`Loom: missing element [data-el="${name}"]`)
    return found as unknown as T
  }

  const nodes: SimNode[] = G.nodes.map((d) => ({ ...d }))
  const byId = new Map<string, SimNode>(nodes.map((n) => [n.id, n]))
  const links: SimLink[] = G.links
    .filter((l) => byId.has(l.source) && byId.has(l.target))
    .map((l) => ({ ...l }))

  // adjacency for backlinks + focus
  const adj = new Map<string, Set<string>>(nodes.map((n) => [n.id, new Set<string>()]))
  for (const l of links) {
    adj.get(l.source as string)?.add(l.target as string)
    adj.get(l.target as string)?.add(l.source as string)
  }

  const state = {
    types: new Set<LoomNodeType>(NODE_TYPES),
    kinds: new Set<LoomLinkKind>(LINK_KINDS),
    focus: '',
    query: '',
    selected: null as string | null,
    timeOn: false,
    cutoff: null as string | null,
  }

  // dates for the timeline
  const allDates = [...new Set(nodes.map((n) => n.born).filter((d): d is string => Boolean(d)))].sort()

  // ── chrome ──────────────────────────────────────────────────────────────
  const railmeta = el<HTMLElement>('railmeta')
  railmeta.textContent =
    `${nodes.filter((n) => n.type === 'agent').length} agents · ${nodes.filter((n) => n.type === 'tool').length} tools · ` +
    `${nodes.filter((n) => n.type === 'namespace').length} namespaces · @${G.meta.commit}`

  el<HTMLElement>('prov').innerHTML =
    `<b style="color:var(--loom-text-dim)">Real</b> — agents, tools, memory namespaces and cadences read from
     <code>src/skills/*.skill.ts</code> and <code>src/lib/spec-drift/thresholds.ts</code>. Dates from git history.<br><br>
     <b style="color:var(--loom-learning)">Mock</b> — the 4 pink learning nodes. Their shape mirrors
     <code>ProposalSchema</code> in <code>loop-reflection.executor.ts</code>; the values are illustrative.<br><br>
     Runtime-internal only. No tenant or product data.`

  const counts = (t: LoomNodeType) => nodes.filter((n) => n.type === t).length
  el<HTMLElement>('typeFilters').innerHTML = NODE_TYPES.map(
    (t) =>
      `<label class="loom-chk"><input type="checkbox" data-type="${t}" checked>
        <span class="loom-dot" style="background:${COLORS[t]}"></span>${TYPE_LABEL[t]}
        <span class="loom-count">${counts(t)}</span></label>`,
  ).join('')
  el<HTMLElement>('linkFilters').innerHTML = LINK_KINDS.map(
    (k) =>
      `<label class="loom-chk"><input type="checkbox" data-kind="${k}" checked>
        <span class="loom-dot" style="background:${LINK_COLORS[k]}"></span>${LINK_LABEL[k]}
        <span class="loom-count">${links.filter((l) => l.kind === k).length}</span></label>`,
  ).join('')
  el<HTMLElement>('legend').innerHTML =
    LINK_KINDS.map((k) => `<span><i style="background:${LINK_COLORS[k]}"></i>${LINK_LABEL[k]}</span>`).join('') +
    `<span style="color:var(--loom-learning)">◌ dashed = mock</span>`

  const agentSel = el<HTMLSelectElement>('agentSel')
  nodes
    .filter((n): n is SimNode & { type: 'agent' } => n.type === 'agent')
    .sort((a, b) => a.label.localeCompare(b.label))
    .forEach((a) => {
      // Built via DOM properties (not an HTML string) so the id/name can never
      // be parsed as markup — `.value`/`.textContent` are assigned verbatim.
      const opt = document.createElement('option')
      opt.value = a.id
      opt.textContent = `${a.label} — ${a.skillName}`
      agentSel.appendChild(opt)
    })

  // ── force sim ───────────────────────────────────────────────────────────
  const svgEl = el<SVGSVGElement>('svg')
  const svg = d3.select(svgEl)
  const rootG = svg.append('g')
  const linkG = rootG.append('g')
  const nodeG = rootG.append('g')

  // While `autoFit` is on, the view keeps re-fitting as the simulation settles
  // so the weave always fills the container. Any manual zoom/pan/drag hands
  // control to the user (Re-center gives it back).
  let autoFit = true

  const zoom = d3
    .zoom<SVGSVGElement, unknown>()
    .scaleExtent([0.15, 4])
    .on('zoom', (e) => {
      // e.sourceEvent is null for programmatic transforms (our own fitView).
      if (e.sourceEvent) autoFit = false
      rootG.attr('transform', e.transform.toString())
    })
  svg.call(zoom)

  const canvas = el<HTMLElement>('canvas')

  /** Live container size — always measured, never a fixed or max width. */
  function dims(): { w: number; h: number } {
    return { w: canvas.clientWidth, h: canvas.clientHeight }
  }

  const radius = (d: SimNode): number =>
    d.type === 'agent'
      ? d.skillName === 'council-ceo'
        ? 15
        : 10
      : d.type === 'namespace'
        ? d.shared
          ? 6
          : 4.5
        : d.type === 'learning'
          ? 6
          : 3.5

  const sim = d3
    .forceSimulation<SimNode>(nodes)
    .force(
      'link',
      d3
        .forceLink<SimNode, SimLink>(links)
        .id((d) => d.id)
        .distance((l) => (l.kind === 'delegates' ? 150 : l.kind === 'owns' ? 42 : 80))
        .strength((l) => (l.kind === 'owns' ? 1 : 0.35)),
    )
    .force('charge', d3.forceManyBody<SimNode>().strength((d) => (d.type === 'agent' ? -600 : -70)))
    .force('collide', d3.forceCollide<SimNode>((d) => radius(d) + 7))
    .force('center', d3.forceCenter(0, 0))
    .force('x', d3.forceX(0).strength(0.02))
    .force('y', d3.forceY(0).strength(0.02))

  const linkSel = linkG
    .selectAll<SVGLineElement, SimLink>('line')
    .data(links)
    .join('line')
    .attr('class', 'link')
    .attr('stroke', (d) => LINK_COLORS[d.kind])
    .attr('stroke-width', (d) => (d.kind === 'delegates' ? 1.4 : 1))
    .attr('stroke-dasharray', (d) => (d.real ? null : '3 3'))

  const nodeSel = nodeG
    .selectAll<SVGGElement, SimNode>('g')
    .data(nodes)
    .join('g')
    .attr('class', (d) => `node ${d.type === 'agent' ? 'is-agent' : ''} ${d.real ? '' : 'mock'}`)
    .on('click', (e: MouseEvent, d) => {
      select(d.id)
      e.stopPropagation()
    })
    .call(
      d3
        .drag<SVGGElement, SimNode>()
        .on('start', (e, d) => {
          autoFit = false // the user is arranging the weave by hand
          if (!e.active) sim.alphaTarget(0.25).restart()
          d.fx = d.x
          d.fy = d.y
        })
        .on('drag', (e, d) => {
          d.fx = e.x
          d.fy = e.y
        })
        .on('end', (e, d) => {
          if (!e.active) sim.alphaTarget(0)
          d.fx = null
          d.fy = null
        }),
    )

  nodeSel
    .append('circle')
    .attr('r', radius)
    .attr('fill', (d) => COLORS[d.type])
  nodeSel
    .append('text')
    .attr('dy', (d) => -radius(d) - 4)
    .text((d) => (d.type === 'agent' || d.type === 'namespace' || d.type === 'learning' ? d.label : ''))

  const onCanvasClick = () => select(null)
  svg.on('click', onCanvasClick)

  let tickCount = 0
  sim.on('tick', () => {
    linkSel
      .attr('x1', (d) => asNode(d.source).x ?? 0)
      .attr('y1', (d) => asNode(d.source).y ?? 0)
      .attr('x2', (d) => asNode(d.target).x ?? 0)
      .attr('y2', (d) => asNode(d.target).y ?? 0)
    nodeSel.attr('transform', (d) => `translate(${d.x ?? 0},${d.y ?? 0})`)

    // Re-fit periodically while the weave is still spreading, otherwise a fit
    // taken mid-settle goes stale and nodes drift outside the viewport.
    tickCount += 1
    if (autoFit && tickCount % 5 === 0) fitView()
  })

  // ── sizing / fit ────────────────────────────────────────────────────────
  // Everything below is driven by the container's real clientWidth/Height, so
  // the graph fills whatever space the layout gives it — no fixed or max width.

  /**
   * Bias the centring forces by the container's aspect ratio so the weave
   * actually spreads across a wide viewport instead of settling into a narrow
   * column: wider container → weaker horizontal pull, stronger vertical pull.
   */
  function applyAspectForces(w: number, h: number) {
    const a = Math.min(Math.max(w / Math.max(h, 1), 0.5), 2.5)
    sim.force('x', d3.forceX<SimNode>(0).strength(0.02 / a))
    sim.force('y', d3.forceY<SimNode>(0).strength(0.02 * a))
  }

  // NOTE: an "aspect stretch" force (easing node x outward until the weave's
  // bounding box matched the container's aspect) was tried here and removed.
  // The graph is hub-and-spoke, so link distance + charge dominate and pull it
  // back to circular; the controller never held a wider equilibrium, and it
  // out-ran the periodic re-fit badly enough to clip nodes off-screen. A
  // uniform fit of a roughly circular graph into a wide box is height-limited
  // by definition — the honest fix is to give it the full container (below),
  // not to distort the layout chasing 100% width.

  /** Zoom/pan so the graph's bounding box fills the current container. */
  function fitView() {
    const { w, h } = dims()
    if (!w || !h || nodes.length === 0) return

    let minX = Infinity
    let maxX = -Infinity
    let minY = Infinity
    let maxY = -Infinity
    for (const n of nodes) {
      if (n.x == null || n.y == null) continue
      const r = radius(n) + 14 // headroom for the node label
      minX = Math.min(minX, n.x - r)
      maxX = Math.max(maxX, n.x + r)
      minY = Math.min(minY, n.y - r)
      maxY = Math.max(maxY, n.y + r)
    }
    if (!Number.isFinite(minX) || !Number.isFinite(minY)) return

    const gw = Math.max(maxX - minX, 1)
    const gh = Math.max(maxY - minY, 1)
    const scale = Math.min(Math.min(w / gw, h / gh) * 0.94, 4)
    const cx = (minX + maxX) / 2
    const cy = (minY + maxY) / 2

    svg.call(
      zoom.transform,
      d3.zoomIdentity
        .translate(w / 2, h / 2)
        .scale(Math.max(scale, 0.15))
        .translate(-cx, -cy),
    )
  }

  /** Re-measure, resize the SVG viewBox, re-bias forces, then re-fit. */
  function applyLayout() {
    const { w, h } = dims()
    if (!w || !h) return
    svg.attr('width', w).attr('height', h).attr('viewBox', `0 0 ${w} ${h}`)
    applyAspectForces(w, h)
    autoFit = true // a new container size always re-fits, then tracks the settle
    sim.alpha(0.3).restart() // let the weave re-spread into the new dimensions
    fitView()
  }

  applyLayout()
  sim.on('end', fitView) // settle, then fit to the real container size

  // Re-fit on any layout change (window resize, sidebar toggle, zoom). rAF
  // coalesces bursts of resize events into one relayout per frame.
  let relayoutFrame = 0
  const scheduleRelayout = () => {
    cancelAnimationFrame(relayoutFrame)
    relayoutFrame = requestAnimationFrame(applyLayout)
  }
  const resizeObserver =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleRelayout) : null
  resizeObserver?.observe(canvas)
  window.addEventListener('resize', scheduleRelayout)
  const recenter = el<HTMLButtonElement>('recenter')
  const onRecenter = () => {
    autoFit = true // hand fitting back to the view
    fitView()
  }
  recenter.addEventListener('click', onRecenter)

  // ── visibility ──────────────────────────────────────────────────────────
  function visibleNode(n: SimNode): boolean {
    if (!state.types.has(n.type)) return false
    if (state.timeOn && state.cutoff && (!n.born || n.born > state.cutoff)) return false
    if (state.focus) {
      if (n.id !== state.focus && !adj.get(state.focus)?.has(n.id)) return false
    }
    if (state.query) {
      const hay = `${n.label} ${'skillName' in n ? n.skillName : ''} ${
        'namespace' in n ? n.namespace : ''
      } ${'description' in n ? n.description : ''}`.toLowerCase()
      if (!hay.includes(state.query)) return false
    }
    return true
  }

  function apply() {
    const vis = new Set(nodes.filter(visibleNode).map((n) => n.id))
    const hot = state.selected ? new Set([state.selected, ...(adj.get(state.selected) ?? [])]) : null

    nodeSel
      .classed('dim', (d) => !vis.has(d.id) || (hot !== null && !hot.has(d.id)))
      .classed('sel', (d) => d.id === state.selected)

    linkSel
      .classed(
        'dim',
        (d) =>
          !state.kinds.has(d.kind) ||
          !vis.has(asNode(d.source).id) ||
          !vis.has(asNode(d.target).id) ||
          (hot !== null && !(hot.has(asNode(d.source).id) && hot.has(asNode(d.target).id))),
      )
      .classed(
        'hot',
        (d) =>
          hot !== null &&
          state.kinds.has(d.kind) &&
          (asNode(d.source).id === state.selected || asNode(d.target).id === state.selected),
      )

    if (state.timeOn) {
      const t = nodes.filter((n) => n.type === 'tool' && vis.has(n.id)).length
      const a = nodes.filter((n) => n.type === 'agent' && vis.has(n.id)).length
      el<HTMLElement>('tstat').textContent = `${a} agents · ${t} tools`
    }
  }

  // ── side panel ──────────────────────────────────────────────────────────
  const panel = el<HTMLElement>('panel')
  const linkPill = (id: string, label: string) => `<span class="pill" data-goto="${esc(id)}">${esc(label)}</span>`

  function select(id: string | null) {
    state.selected = id
    apply()
    if (!id) {
      panel.className = 'loom-panel empty'
      panel.textContent = 'Select a node to inspect it.'
      return
    }
    const node = byId.get(id)
    if (!node) return
    panel.className = 'loom-panel'
    panel.innerHTML = render(node)
    panel.querySelectorAll<HTMLElement>('[data-goto]').forEach((n) => {
      const goto = n.dataset.goto
      if (goto) n.addEventListener('click', () => select(goto))
    })
    panel.scrollTop = 0
  }

  function render(n: SimNode): string {
    if (n.type === 'agent') return renderAgent(n)
    if (n.type === 'tool') return renderTool(n)
    if (n.type === 'namespace') return renderNs(n)
    return renderLearning(n)
  }

  function renderAgent(n: SimNode & { type: 'agent' }): string {
    const tools = nodes.filter((t) => t.type === 'tool' && t.owner === n.skillName)
    const reads = links.filter((l) => asNode(l.source).id === n.id && l.kind === 'reads').map((l) => asNode(l.target))
    const writes = links.filter((l) => asNode(l.source).id === n.id && l.kind === 'writes').map((l) => asNode(l.target))
    const learns = links.filter((l) => l.kind === 'learned' && asNode(l.source).id === n.id).map((l) => asNode(l.target))
    const delegatedBy = links
      .filter((l) => l.kind === 'delegates' && asNode(l.target).id === n.id)
      .map((l) => asNode(l.source))
    const delegatesTo = links
      .filter((l) => l.kind === 'delegates' && asNode(l.source).id === n.id)
      .map((l) => asNode(l.target))
    const cls =
      n.agentClass === 'fixture'
        ? 'Test fixture — not a shipped agent'
        : 'Council · ON the business (strategic / meta)'

    return `
      <div class="kicker">Agent</div>
      <h3>${esc(n.label)}</h3>
      <div class="meta">${esc(n.displayName)} · <code>${esc(n.skillName)}</code> · v${esc(n.version)}</div>
      <div style="margin-bottom:12px"><span class="realbadge">REAL — from skill config</span></div>
      <div class="desc">${esc(n.description)}</div>

      <div class="sec"><h4>Identity</h4>
        <div class="row"><span>Class</span><span>${esc(cls)}</span></div>
        <div class="row"><span>Model</span><span>${esc(n.model)}</span></div>
        <div class="row"><span>First shipped</span><span>${esc(n.born || '—')}</span></div>
        <div class="row"><span>Source</span><span><code>${esc(n.file)}</code></span></div>
      </div>

      <div class="sec"><h4>Loop discipline (learning contract)</h4>
        ${
          n.loop.onRoster
            ? `<div class="row"><span>On learning roster</span><span>yes — COUNCIL_THRESHOLDS</span></div>
               <div class="row"><span>Cadence</span><span>${esc(n.loop.cadence)}</span></div>
               <div class="row"><span>Stale after</span><span>${n.loop.staleAfterDays ?? 'n/a (event-driven)'} days</span></div>`
            : `<div class="row"><span>On learning roster</span><span>no — excluded from Loop Reflection</span></div>`
        }
      </div>

      <div class="sec"><h4>Skills / tools (${tools.length})</h4>
        ${tools.map((t) => linkPill(t.id, t.label)).join('') || '<span class="prov">none</span>'}
      </div>

      <div class="sec"><h4>Writes memory (${writes.length})</h4>
        ${writes.map((t) => linkPill(t.id, t.label)).join('') || '<span class="prov">none</span>'}
      </div>
      <div class="sec"><h4>Reads memory (${reads.length})</h4>
        ${reads.map((t) => linkPill(t.id, t.label)).join('') || '<span class="prov">none</span>'}
      </div>

      ${
        delegatesTo.length
          ? `<div class="sec"><h4>Delegates to (${delegatesTo.length}) — via trigger_council_agent</h4>
             ${delegatesTo.map((t) => linkPill(t.id, t.label)).join('')}</div>`
          : ''
      }
      ${
        delegatedBy.length
          ? `<div class="sec"><h4>Delegated by</h4>${delegatedBy.map((t) => linkPill(t.id, t.label)).join('')}</div>`
          : ''
      }

      <div class="sec"><h4>Triggers</h4>
        ${
          n.triggers.cron.length
            ? n.triggers.cron
                .map((c) => `<div class="row"><span><code>${esc(c.expression)}</code></span><span>${esc(c.description)}</span></div>`)
                .join('')
            : '<div class="prov">No cron — event-driven only.</div>'
        }
        ${
          n.triggers.events.length
            ? `<div style="margin-top:6px">${n.triggers.events.map((e) => `<span class="pill static">${esc(e)}</span>`).join('')}</div>`
            : ''
        }
      </div>

      <div class="sec"><h4>Decision authority</h4>
        <div class="row"><span>Max autonomous actions</span><span>${n.decision.maxAutonomousActions}</span></div>
        <div class="row"><span>Safe default</span><span>${esc(n.decision.safeDefault)}</span></div>
        <div style="margin-top:6px">${n.decision.requiresApprovalFor.map((r) => `<span class="pill static">${esc(r)}</span>`).join('')}</div>
      </div>

      ${
        learns.length
          ? `<div class="sec"><h4>Learnings <span class="mockbadge">MOCK</span></h4>
             ${learns.map((t) => linkPill(t.id, `${t.label} · ${'week' in t ? t.week : ''}`)).join('')}</div>`
          : ''
      }
    `
  }

  function renderTool(n: SimNode & { type: 'tool' }): string {
    const owner = byId.get(`agent:${n.owner}`)
    const sameName = nodes.filter((t) => t.type === 'tool' && t.toolName === n.toolName)
    return `
      <div class="kicker">Skill / tool</div>
      <h3>${esc(n.label)}</h3>
      <div class="meta">owned by ${esc(owner?.label)} · <code>${esc(n.owner)}</code></div>
      <div style="margin-bottom:12px"><span class="realbadge">REAL — declared in skill config</span></div>
      <div class="desc">${esc(n.description)}</div>
      <div class="sec"><h4>Provenance</h4>
        <div class="row"><span>First appeared</span><span>${esc(n.born || '—')}${n.bornApprox ? ' (approx.)' : ''}</span></div>
        ${
          n.bornApprox
            ? `<div class="prov" style="margin-top:5px">Approximate: no isolated git commit matched this tool name, so it inherits its agent's first-shipped date.</div>`
            : ''
        }
      </div>
      ${owner ? `<div class="sec"><h4>Owner</h4>${linkPill(owner.id, owner.label)}</div>` : ''}
      ${
        sameName.length > 1
          ? `<div class="sec"><h4>Also declared by (${sameName.length - 1}) — shared tool</h4>
             ${sameName
               .filter((t) => t.id !== n.id)
               .map((t) => {
                 const o = byId.get(`agent:${(t as SimNode & { type: 'tool' }).owner}`)
                 return o ? linkPill(o.id, o.label) : ''
               })
               .join('')}</div>`
          : ''
      }
    `
  }

  function renderNs(n: SimNode & { type: 'namespace' }): string {
    const r = n.readers.map((s) => byId.get(`agent:${s}`)).filter((x): x is SimNode => Boolean(x))
    const w = n.writers.map((s) => byId.get(`agent:${s}`)).filter((x): x is SimNode => Boolean(x))
    const orphan = w.length === 0
    return `
      <div class="kicker">Memory namespace</div>
      <h3>${esc(n.label)}</h3>
      <div class="meta">${n.wildcard ? 'wildcard pattern' : 'exact namespace'} · keys stored as <code>${esc(n.namespace)}/{key}</code></div>
      <div style="margin-bottom:12px"><span class="realbadge">REAL — from skill memory config</span></div>
      ${
        orphan
          ? `<div class="warn"><b>No declared writer.</b> Every agent below reads this namespace but no shipped skill config declares write access to it — either it is written by an executor outside the skill's declared config, or the reads resolve to nothing.</div>`
          : ''
      }
      ${
        n.shared
          ? `<div class="sec"><h4>Shared</h4><div class="prov">Touched by ${r.length + w.length} agent bindings — a cross-agent handoff point.</div></div>`
          : ''
      }
      <div class="sec"><h4>Written by (${w.length})</h4>
        ${w.map((a) => linkPill(a.id, a.label)).join('') || '<span class="prov">none declared</span>'}</div>
      <div class="sec"><h4>Read by (${r.length})</h4>
        ${r.map((a) => linkPill(a.id, a.label)).join('') || '<span class="prov">none declared</span>'}</div>
    `
  }

  function renderLearning(n: SimNode & { type: 'learning' }): string {
    const agent = byId.get(`agent:${n.agent}`)
    return `
      <div class="kicker">Learning / reflection proposal</div>
      <h3>${esc(n.label)}</h3>
      <div class="meta">${esc(n.week)} · about ${esc(agent?.label)}</div>
      <div style="margin-bottom:12px"><span class="mockbadge">MOCK — ILLUSTRATIVE, NOT OBSERVED</span></div>
      <div class="warn">
        This node is <b>fabricated sample data</b>. Its shape is real — it matches <code>ProposalSchema</code>
        in <code>src/executors/loop-reflection.executor.ts</code> (<code>pattern</code>,
        <code>proposalType</code>, <code>proposal</code>) — but no such reflection has been read from the
        runtime. Nothing here reflects actual agent behaviour.
      </div>
      <div class="sec"><h4>Pattern observed</h4><div class="desc">${esc(n.pattern)}</div></div>
      <div class="sec"><h4>Proposal (propose-only)</h4><div class="desc">${esc(n.proposal)}</div></div>
      <div class="sec"><h4>How this would work live</h4>
        <div class="prov">
          Asclepius runs weekly Loop Reflection. It reads <code>loop_discipline_escalation</code> rows from
          <code>athena_activity_log</code> via <code>getCouncilLoopStats()</code>, and product-side rows over the
          Athena bridge. Agents with ≥3 failures in the trailing 7 days get one capped Haiku call that drafts at
          most one proposal, written to memory key <code>asclepius/loop-reflection/latest</code> and queued as a
          Decision for Mark.
        </div>
      </div>
      ${agent ? `<div class="sec"><h4>Subject</h4>${linkPill(agent.id, agent.label)}</div>` : ''}
      ${byId.has('agent:dev-ops') ? `<div class="sec"><h4>Author</h4>${linkPill('agent:dev-ops', 'Asclepius')}</div>` : ''}
    `
  }

  // ── controls ────────────────────────────────────────────────────────────
  const typeFilters = el<HTMLElement>('typeFilters')
  const onTypeChange = (e: Event) => {
    const target = e.target as HTMLInputElement
    const t = target.dataset.type as LoomNodeType | undefined
    if (!t) return
    target.checked ? state.types.add(t) : state.types.delete(t)
    apply()
  }
  typeFilters.addEventListener('change', onTypeChange)

  const linkFilters = el<HTMLElement>('linkFilters')
  const onLinkChange = (e: Event) => {
    const target = e.target as HTMLInputElement
    const k = target.dataset.kind as LoomLinkKind | undefined
    if (!k) return
    target.checked ? state.kinds.add(k) : state.kinds.delete(k)
    apply()
  }
  linkFilters.addEventListener('change', onLinkChange)

  const onAgentSel = (e: Event) => {
    state.focus = (e.target as HTMLSelectElement).value
    apply()
  }
  agentSel.addEventListener('change', onAgentSel)

  const search = el<HTMLInputElement>('search')
  const onSearch = (e: Event) => {
    state.query = (e.target as HTMLInputElement).value.trim().toLowerCase()
    apply()
  }
  search.addEventListener('input', onSearch)

  // ── timeline ────────────────────────────────────────────────────────────
  const timebar = el<HTMLElement>('timebar')
  const slider = el<HTMLInputElement>('tslider')
  const tdate = el<HTMLElement>('tdate')
  const play = el<HTMLButtonElement>('play')
  let playTimer: ReturnType<typeof setInterval> | null = null

  const cutoffFor = (pct: number): string => allDates[Math.round((pct / 100) * (allDates.length - 1))]
  function setCutoff(pct: number) {
    state.cutoff = cutoffFor(pct)
    tdate.textContent = state.cutoff
    apply()
  }
  function stopPlay() {
    if (playTimer) {
      clearInterval(playTimer)
      playTimer = null
      play.textContent = '▶ Play'
    }
  }

  const timeOn = el<HTMLInputElement>('timeOn')
  const onTimeOn = (e: Event) => {
    state.timeOn = (e.target as HTMLInputElement).checked
    timebar.classList.toggle('off', !state.timeOn)
    if (state.timeOn) {
      slider.value = '100'
      setCutoff(100)
    } else {
      stopPlay()
      state.cutoff = null
      apply()
    }
  }
  timeOn.addEventListener('change', onTimeOn)

  const onSlider = (e: Event) => {
    stopPlay()
    setCutoff(+(e.target as HTMLInputElement).value)
  }
  slider.addEventListener('input', onSlider)

  const onPlay = () => {
    if (playTimer) return stopPlay()
    play.textContent = '⏸ Pause'
    let i = 0
    slider.value = '0'
    setCutoff(0)
    playTimer = setInterval(() => {
      i += 1
      if (i > allDates.length - 1) return stopPlay()
      slider.value = String((i / (allDates.length - 1)) * 100)
      setCutoff(+slider.value)
    }, 320)
  }
  play.addEventListener('click', onPlay)

  apply()

  // ── cleanup ─────────────────────────────────────────────────────────────
  return () => {
    stopPlay()
    sim.stop()
    cancelAnimationFrame(relayoutFrame)
    resizeObserver?.disconnect()
    window.removeEventListener('resize', scheduleRelayout)
    recenter.removeEventListener('click', onRecenter)
    typeFilters.removeEventListener('change', onTypeChange)
    linkFilters.removeEventListener('change', onLinkChange)
    agentSel.removeEventListener('change', onAgentSel)
    search.removeEventListener('input', onSearch)
    timeOn.removeEventListener('change', onTimeOn)
    slider.removeEventListener('input', onSlider)
    play.removeEventListener('click', onPlay)
    svg.on('click', null)
    svg.on('.zoom', null)
    rootG.remove()
  }
}
